import {
  titleUI,
  withEditTitle,
} from 'platform/forms-system/src/js/web-component-patterns';
import get from 'platform/utilities/data/get';
import React from 'react';
import {
  capitalizeFirst,
  formatFullName,
  makePossessive,
  replaceStrValues,
} from '../utils';

const CASING = {
  capitalize: capitalizeFirst,
  lowercase: value => value.charAt(0).toLowerCase() + value.slice(1),
  preserve: value => value,
};

const NAME_FORMATTERS = {
  first: name => name?.first || '',
  full: name => formatFullName(name),
};

const DEFAULT_OPTS = {
  placeholder: '%s',
  casing: 'capitalize',
  editCasing: 'preserve',

  roleKey: 'certifierRole',
  matchRole: 'applicant',
  self: 'your',
  other: 'their',

  nameKey: 'applicantName',
  nameFormat: 'first',
  possessive: true,

  dataKey: '',
  fallback: '',

  arrayBuilder: false,
  privacy: false,
  classNames: undefined,
  piiClassNames:
    'vads-u-color--black vads-u-margin-top--0 mobile-lg:vads-u-font-size--h2 vads-u-font-size--h3 dd-privacy-mask',
};

const hasValue = value => value !== null && value !== undefined;

const normalizeFactoryDefaults = ({
  defaultRoleKey,
  defaultMatchRole,
  defaultSelf,
  defaultOther,
  defaultNameKey,
  defaultNameFormat,
  defaultPiiClassNames,
  ...defaults
} = {}) => ({
  ...defaults,
  ...(hasValue(defaultRoleKey) && { roleKey: defaultRoleKey }),
  ...(hasValue(defaultMatchRole) && { matchRole: defaultMatchRole }),
  ...(hasValue(defaultSelf) && { self: defaultSelf }),
  ...(hasValue(defaultOther) && { other: defaultOther }),
  ...(hasValue(defaultNameKey) && { nameKey: defaultNameKey }),
  ...(hasValue(defaultNameFormat) && { nameFormat: defaultNameFormat }),
  ...(hasValue(defaultPiiClassNames) && {
    piiClassNames: defaultPiiClassNames,
  }),
});

const normalizeString = value => {
  if (value == null) return '';
  return typeof value === 'string' ? value.trim() : String(value);
};

const normalizeContext = input => ({
  formData: input?.formData || input || {},
  itemData: input?.itemData,
  index: input?.index,
  props: input,
});

const mergeOpts = (defaults, opts = {}) => ({
  ...DEFAULT_OPTS,
  ...defaults,
  ...opts,
});

const applyCasing = (value, casing) =>
  value ? (CASING[casing] || CASING.capitalize)(value) : value;

const isSelf = (formData, opts) => formData?.[opts.roleKey] === opts.matchRole;

const getNameValue = (nameObj, nameFormat) =>
  (NAME_FORMATTERS[nameFormat] || NAME_FORMATTERS.first)(nameObj);

const fillTemplate = (template, value, opts) => {
  if (!template) return '';
  if (!value) return template;
  return replaceStrValues(template, value, opts.placeholder);
};

const formatReplacement = (resolvedValue, opts) => {
  const { value: rawValue, possessive = opts.possessive } =
    resolvedValue && typeof resolvedValue === 'object'
      ? resolvedValue
      : { value: resolvedValue };

  const value = normalizeString(rawValue) || normalizeString(opts.fallback);
  if (!value) return '';

  const possessiveValue = possessive ? makePossessive(value) : value;
  return applyCasing(possessiveValue, opts.casing);
};

const resolveRoleValue = (context, opts) => {
  const self = isSelf(context.formData, opts);
  return {
    value: self ? opts.self : opts.other,
    possessive: !self && opts.possessive,
  };
};

const resolveNameValue = (context, opts) => {
  if (isSelf(context.formData, opts)) {
    return { value: opts.self, possessive: false };
  }

  const nameObj = context.formData?.[opts.nameKey] || {};
  return {
    value: getNameValue(nameObj, opts.nameFormat) || opts.other,
    possessive: opts.possessive,
  };
};

const resolveFormDataValue = (context, opts) => {
  let value = opts.fallback;

  if (opts.dataKey) {
    value =
      typeof opts.dataKey === 'function'
        ? opts.dataKey(context)
        : get(opts.dataKey, context.formData);
  }

  return {
    value,
    possessive: opts.possessive,
  };
};

const resolveItemNameValue = context =>
  context.props?.getItemName?.(
    context.props.itemData,
    context.props.index,
    context.props.formData,
  );

const buildTitle = (template, resolver, input, opts) => {
  const context = normalizeContext(input);
  const replacement = formatReplacement(resolver(context, opts), opts);
  return fillTemplate(template, replacement, opts);
};

// eslint-disable-next-line react/prop-types
export const PrivacyContent = ({ children }) => (
  <span data-dd-privacy="mask">{children}</span>
);

const renderContent = (text, opts) =>
  opts.privacy ? <PrivacyContent>{text}</PrivacyContent> : text;

const renderTitleUI = (template, resolver, description, opts) => {
  const titleText = input => buildTitle(template, resolver, input, opts);

  const formattedTitle = opts.arrayBuilder
    ? withEditTitle(titleText, opts.editCasing === 'lowercase')
    : titleText;

  const title = input => renderContent(formattedTitle(input), opts);

  return titleUI({
    title,
    description,
    classNames: opts.privacy ? opts.piiClassNames : opts.classNames,
  });
};

/**
 * Creates a factory that generates title pattern helper functions with customizable defaults.
 *
 * This factory function returns a suite of helper functions for creating dynamic titles
 * in forms, with support for role-based text, name formatting, and form data interpolation.
 * All returned functions support placeholder replacement, casing transformations, and
 * privacy masking.
 *
 * @param {Object} [factoryOptions] - Default configuration for all title pattern functions.
 * @param {string} [factoryOptions.defaultRoleKey] - Default key to check for role comparison.
 * @param {string} [factoryOptions.defaultMatchRole] - Default role value to match against for "self" detection.
 * @param {string} [factoryOptions.defaultSelf] - Default text to use when role matches (e.g., "your").
 * @param {string} [factoryOptions.defaultOther] - Default text to use when role doesn't match.
 * @param {string} [factoryOptions.defaultNameKey] - Default key to retrieve name object from form data.
 * @param {string} [factoryOptions.defaultNameFormat] - Default name format ('first' or 'full').
 * @param {string} [factoryOptions.defaultPiiClassNames] - Default CSS classes for privacy-masked content.
 *
 * @returns {Object} Object containing title pattern helper functions.
 * @returns {Function} return.titleWithRoleUI - Creates titleUI based on role (self vs other).
 * @returns {Function} return.titleWithNameUI - Creates titleUI with name interpolation and privacy masking.
 * @returns {Function} return.titleWithFormDataUI - Creates titleUI using form data values.
 * @returns {Function} return.titleWithRoleContent - Creates role-based content (returns React element).
 * @returns {Function} return.titleWithNameContent - Creates name-based content with privacy masking.
 * @returns {Function} return.titleWithFormDataContent - Creates form data-based content.
 * @returns {Function} return.titleWithRoleText - Creates role-based plain text string.
 * @returns {Function} return.titleWithFormDataText - Creates form data-based plain text string.
 * @returns {Function} return.itemOrNounText - Creates text using ArrayBuilder item name or fallback noun.
 * @returns {Function} return.PrivacyContent - React component for privacy-masked content.
 *
 * @example
 * const { titleWithRoleUI, titleWithNameUI } = makeTitlePatterns({
 *   defaultRoleKey: 'userRole',
 *   defaultSelf: 'your',
 *   defaultOther: 'their'
 * });
 *
 * // Returns UI with "your" or "their" based on role
 * titleWithRoleUI('Enter %s address');
 *
 * @example
 * const { titleWithNameUI } = makeTitlePatterns({
 *   defaultNameKey: 'veteranName',
 *   defaultNameFormat: 'first'
 * });
 *
 * // Returns UI with "John's address" (privacy-masked)
 * titleWithNameUI('%s address', null, { possessive: true });
 */
export const makeTitlePatterns = (factoryOptions = {}) => {
  const defaults = normalizeFactoryDefaults(factoryOptions);
  const withOpts = opts => mergeOpts(defaults, opts);

  const titleWithRoleUI = (title, description = null, options = {}) =>
    renderTitleUI(title, resolveRoleValue, description, withOpts(options));

  const titleWithNameUI = (title, description = null, options = {}) =>
    renderTitleUI(
      title,
      resolveNameValue,
      description,
      withOpts({ privacy: true, ...options }),
    );

  const titleWithFormDataUI = (title, description = null, options = {}) =>
    renderTitleUI(
      title,
      resolveFormDataValue,
      description,
      withOpts({ possessive: false, ...options }),
    );

  const titleWithRoleContent =
    (title, options = {}) =>
    input => {
      const opts = withOpts(options);
      return renderContent(
        buildTitle(title, resolveRoleValue, input, opts),
        opts,
      );
    };

  const titleWithNameContent =
    (title, options = {}) =>
    input => {
      const opts = withOpts({ privacy: true, ...options });
      return renderContent(
        buildTitle(title, resolveNameValue, input, opts),
        opts,
      );
    };

  const titleWithFormDataContent =
    (title, options = {}) =>
    input => {
      const opts = withOpts({ possessive: false, ...options });

      return renderContent(
        buildTitle(title, resolveFormDataValue, input, opts),
        opts,
      );
    };

  const titleWithRoleText =
    (title, options = {}) =>
    input =>
      buildTitle(title, resolveRoleValue, input, withOpts(options));

  const titleWithFormDataText =
    (title, options = {}) =>
    input =>
      buildTitle(
        title,
        resolveFormDataValue,
        input,
        withOpts({ possessive: false, ...options }),
      );

  const itemOrNounText =
    ({ item, noun, placeholder } = {}) =>
    props => {
      const opts = withOpts({ placeholder });
      const context = normalizeContext(props);
      const itemName = resolveItemNameValue(context);
      return itemName
        ? fillTemplate(item, itemName, opts)
        : fillTemplate(noun, props.nounSingular, opts);
    };

  return {
    titleWithRoleUI,
    titleWithNameUI,
    titleWithFormDataUI,

    titleWithRoleContent,
    titleWithNameContent,
    titleWithFormDataContent,

    titleWithRoleText,
    titleWithFormDataText,

    itemOrNounText,
    PrivacyContent,
  };
};
